import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAddEvent } from "../../../../hooks/track/useAddEvent";
import { useUpdateEvent } from "../../../../hooks/track/useUpdateEvent";
import { useUserProviders } from "../../../../hooks/useUserProviders";
import { useAnalytics } from "../../../../lib/analytics";
import { useClientTranslations } from "../../../../lib/i18n/client";
import {
	TRACK_EVENT_CREATED,
	TRACK_EVENT_EDITED,
} from "../../../../lib/track/analyticsEvents";
import { parseSyntheticEventId } from "../../../../lib/track/parseSyntheticEventId";
import {
	type AppointmentTestFormState,
	validateAppointmentTestForm,
} from "../../../../services/formValidationService";
import type { CreateEventInput, Event } from "../../../../types/event";
import {
	ContactPicker,
	DatePicker,
	FormField,
	Select,
	type SelectOption,
	TimePicker,
} from "../fields";
import { RecurringSeriesBanner } from "../RecurringSeriesBanner";
import styles from "./AppointmentTestForm.module.css";
import { EventFormShell } from "./EventFormShell";

type FormMode = "create" | "edit";

interface AppointmentTestFormProps {
	formType: "appointment" | "test";
	mode: FormMode;
	initialValues?: Event;
	onClose: () => void;
	/**
	 * When true, render an internal Appointment/Test pill toggle at the top so
	 * the user can switch between the two modes after picking the merged
	 * "Test / Appointment" chooser card. Edit mode disables this — the existing
	 * event's type is fixed.
	 */
	allowFormTypeToggle?: boolean;
}

// Curated appointment subtype keys (RESEARCH §"Curated dropdown values").
const APPT_SUBTYPES = [
	"initialConsultation",
	"cycleReview",
	"monitoringScan",
	"eggRetrieval",
	"embryoTransfer",
	"pregnancyTest",
	"followUp",
	"geneticCounseling",
	"mockCycle",
	"other",
] as const;

// Curated test subtype keys.
const TEST_SUBTYPES = [
	"bloodworkBaseline",
	"bloodworkEstradiol",
	"bloodworkProgesterone",
	"bloodworkBetaHcg",
	"ultrasoundAfc",
	"ultrasoundMonitoring",
	"hsg",
	"salineSonogram",
	"semenAnalysis",
	"geneticCarrierScreen",
	"other",
] as const;

function formatDateTime(date: Date, time: string): string {
	const yyyy = date.getFullYear();
	const mm = String(date.getMonth() + 1).padStart(2, "0");
	const dd = String(date.getDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}T${time}:00`;
}

function getDeviceTimezone(): string {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch {
		return "America/New_York";
	}
}

/**
 * Phase 37 — Appointment + Test form (RESEARCH Q2 — single component, formType prop).
 *
 * Field order (UI-SPEC §Body — form): Subtype → Date → Time → Location → Contact → Notes.
 *
 * Save lifecycle:
 *  - validate state via validateAppointmentTestForm
 *  - title composition: appointment ⇒ "{subtype} — {provider}", test ⇒ "{subtype}"
 *  - dateTime: wall-clock-naive `YYYY-MM-DDTHH:mm:00`; timezone derived from device IANA
 *  - create mode: useRef-held UUIDv4 Idempotency-Key (Pattern 2; reset on success/error)
 *  - edit mode: parseSyntheticEventId → baseId before PUT (Pitfall 2)
 *  - on success: enums-only logEvent (TRACK_EVENT_CREATED|EDITED) + static-key toast + onClose
 *  - on error: static-key toast + reset Idempotency-Key
 */
export function AppointmentTestForm({
	formType,
	mode,
	initialValues,
	onClose,
	allowFormTypeToggle = false,
}: AppointmentTestFormProps) {
	const { t } = useClientTranslations("track");
	const { logEvent } = useAnalytics();
	const { data: providersData } = useUserProviders();
	const providers = providersData?.providers ?? [];

	const [state, setState] = useState<AppointmentTestFormState>(() => {
		if (initialValues) {
			const dt = new Date(initialValues.dateTime);
			const hh = String(dt.getHours()).padStart(2, "0");
			const mm = String(dt.getMinutes()).padStart(2, "0");
			return {
				formType,
				subtype: initialValues.subtype ?? null,
				date: dt,
				time: `${hh}:${mm}`,
				location: initialValues.location ?? "",
				contactId: initialValues.contactId ?? null,
				notes: initialValues.notes ?? "",
			};
		}
		return {
			formType,
			subtype: null,
			date: null,
			time: null,
			location: "",
			contactId: null,
			notes: "",
		};
	});

	// If formType prop changes (parent re-keyed), keep state.formType in sync —
	// but only when toggle is NOT user-controlled. With the toggle on, the user
	// owns the formType and we must not clobber their selection.
	useEffect(() => {
		if (allowFormTypeToggle) return;
		setState((s) => (s.formType === formType ? s : { ...s, formType }));
	}, [formType, allowFormTypeToggle]);

	const handleToggleFormType = (next: "appointment" | "test") => {
		setState((s) =>
			s.formType === next ? s : { ...s, formType: next, subtype: null },
		);
	};

	const idempotencyKeyRef = useRef<string | null>(null);
	const addMutation = useAddEvent();
	const updateMutation = useUpdateEvent();
	const isPending = addMutation.isPending || updateMutation.isPending;
	const validation = validateAppointmentTestForm(state);
	const activeFormType = state.formType;
	const subtypeOptions =
		activeFormType === "appointment" ? APPT_SUBTYPES : TEST_SUBTYPES;
	const subtypeListKey =
		activeFormType === "appointment" ? "appointmentType" : "testType";

	// Phase 37 D-19 — render whole-series banner above body in edit mode when synthetic id detected.
	const isRecurringInstance =
		mode === "edit" && initialValues
			? parseSyntheticEventId(initialValues.id) !== null
			: false;

	const handleSave = () => {
		if (!validation.isValid) return;
		const provider =
			providers.find((p) => p.providerUserId === state.contactId) ?? null;
		// Preserve previously stored doctorName when the user did not change the
		// contact AND the providers query has not resolved yet — without this,
		// saving an unchanged edit before useUserProviders hydrates would send
		// doctorName: null and silently erase prescriber metadata (PR-102 review #2).
		const contactUnchanged =
			mode === "edit" && (initialValues?.contactId ?? null) === state.contactId;
		const doctorName =
			provider?.providerName ??
			(contactUnchanged ? (initialValues?.doctorName ?? null) : null);
		const subtypeLabel = state.subtype
			? t(`event.add.subtypes.${subtypeListKey}.${state.subtype}`)
			: "";
		const title =
			activeFormType === "appointment" && doctorName
				? `${subtypeLabel} — ${doctorName}`
				: subtypeLabel;
		if (!state.date || !state.time) return;
		const dateTime = formatDateTime(state.date, state.time);

		const body: CreateEventInput = {
			type: activeFormType,
			subtype: state.subtype,
			title,
			location: state.location.trim() || null,
			notes: state.notes.trim() || null,
			doctorName,
			dateTime,
			timezone: initialValues?.timezone ?? getDeviceTimezone(),
			contactId: state.contactId,
		};

		if (mode === "create") {
			if (!idempotencyKeyRef.current) {
				idempotencyKeyRef.current = crypto.randomUUID();
			}
			addMutation.mutate(
				{ body, idempotencyKey: idempotencyKeyRef.current },
				{
					onSuccess: () => {
						idempotencyKeyRef.current = null;
						logEvent(TRACK_EVENT_CREATED, undefined, {
							eventType: activeFormType,
							isRecurring: false,
						});
						toast.success(t("event.add.successToast"));
						onClose();
					},
					onError: () => {
						idempotencyKeyRef.current = null;
						toast.error(t("event.add.errors.save"));
					},
				},
			);
			return;
		}

		if (initialValues) {
			const baseId =
				parseSyntheticEventId(initialValues.id)?.baseId ?? initialValues.id;
			updateMutation.mutate(
				{ id: baseId, patch: body },
				{
					onSuccess: () => {
						logEvent(TRACK_EVENT_EDITED, undefined, {
							eventType: activeFormType,
							isRecurring: !!initialValues.frequencyRRULE,
						});
						toast.success(t("event.edit.successToast"));
						onClose();
					},
					onError: () => {
						toast.error(t("event.edit.errorToast"));
					},
				},
			);
		}
	};

	const subtypeId = `${formType}-subtype`;
	const dateId = `${formType}-date`;
	const timeId = `${formType}-time`;
	const locationId = `${formType}-location`;
	const contactId = `${formType}-contact`;
	const notesId = `${formType}-notes`;

	return (
		<EventFormShell
			mode={mode}
			isValid={validation.isValid}
			isPending={isPending}
			onSave={handleSave}
			recurringBanner={
				isRecurringInstance && initialValues ? (
					<RecurringSeriesBanner type={initialValues.type} variant="edit" />
				) : undefined
			}
		>
			{allowFormTypeToggle ? (
				<fieldset className={styles.formTypeToggle}>
					<legend className={styles.srOnly}>
						{t("event.add.appointmentTest.toggleLegend")}
					</legend>
					<button
						type="button"
						className={`${styles.formTypePill} ${
							activeFormType === "appointment"
								? styles.formTypePillSelected
								: ""
						}`}
						aria-pressed={activeFormType === "appointment"}
						onClick={() => handleToggleFormType("appointment")}
					>
						{t("event.add.appointmentTest.toggle.appointment")}
					</button>
					<button
						type="button"
						className={`${styles.formTypePill} ${
							activeFormType === "test" ? styles.formTypePillSelected : ""
						}`}
						aria-pressed={activeFormType === "test"}
						onClick={() => handleToggleFormType("test")}
					>
						{t("event.add.appointmentTest.toggle.test")}
					</button>
				</fieldset>
			) : null}
			<FormField
				id={subtypeId}
				label={t(`event.add.fields.subtype.${activeFormType}.label`)}
				required
			>
				<Select
					id={subtypeId}
					value={state.subtype}
					options={subtypeOptions.map(
						(key): SelectOption => ({
							value: key,
							label: t(`event.add.subtypes.${subtypeListKey}.${key}`),
						}),
					)}
					placeholder={t("event.add.fields.subtype.placeholder")}
					onChange={(v) => setState((s) => ({ ...s, subtype: v || null }))}
				/>
			</FormField>

			<DatePicker
				id={dateId}
				label={t("event.add.fields.date.label")}
				required
				selected={state.date ?? undefined}
				onSelect={(d) => setState((s) => ({ ...s, date: d ?? null }))}
			/>

			<TimePicker
				id={timeId}
				label={t("event.add.fields.time.label")}
				required
				value={state.time ?? undefined}
				onChange={(v) => setState((s) => ({ ...s, time: v }))}
			/>

			<FormField
				id={locationId}
				label={t("event.add.fields.location.label")}
				required
			>
				<input
					id={locationId}
					type="text"
					className={styles.input}
					value={state.location}
					placeholder={t("event.add.fields.location.placeholder")}
					onChange={(e) =>
						setState((s) => ({ ...s, location: e.target.value }))
					}
				/>
			</FormField>

			<ContactPicker
				id={contactId}
				label={t("event.add.fields.contact.label")}
				required
				value={state.contactId}
				onSelect={(providerUserId) =>
					setState((s) => ({ ...s, contactId: providerUserId }))
				}
			/>

			<FormField id={notesId} label={t("event.add.fields.notes.label")}>
				<textarea
					id={notesId}
					className={styles.textarea}
					value={state.notes}
					placeholder={t("event.add.fields.notes.placeholder")}
					onChange={(e) => setState((s) => ({ ...s, notes: e.target.value }))}
				/>
			</FormField>
		</EventFormShell>
	);
}
